import { DASHBOARD_SKIP_CACHE } from "@/core/constants/dashboard.constants";
import {
    adaptDashboardWidgetData,
    keysToCamelCase,
} from "@/core/helpers/dashboard-api-adapter";
import {
    apiServiceInstance,
    getIdDonViFromLocalStorage,
} from "@/core/helpers/utils";
import type {
    DashboardAlertsData,
    DashboardCacheVersions,
    DashboardEnvelope,
    DashboardMapData,
    DashboardMetaData,
    DashboardMovementGroupBy,
    DashboardMovementMetric,
    DashboardRecentActivitiesData,
    DashboardResponseMeta,
    DashboardScope,
    DashboardScopeQueryParams,
    DashboardSummaryData,
    DashboardSystemSummaryData,
    DashboardTicketStatusesData,
    DashboardWidgetName,
    InventoryMovementData,
    TopEntitiesData,
} from "@/core/interfaces/Dashboard";

type AxiosDataEnvelope<T> = {
  status?: number;
  message?: string;
  data: T;
  meta?: DashboardResponseMeta;
  extra?: {
    cache?: DashboardResponseMeta["cache"];
    scope?: DashboardResponseMeta["scope"];
    meta?: DashboardResponseMeta;
  };
};

function emptyCacheMeta(): DashboardResponseMeta["cache"] {
  return {
    version: "",
    generatedAt: "",
    expiresAt: "",
    ttlSeconds: 0,
    isStale: false,
  };
}

function emptyScopeMeta(
  params?: Partial<DashboardScopeQueryParams>
): DashboardResponseMeta["scope"] {
  return {
    type: (params?.scope_type ?? "national") as DashboardScope["type"],
    idDonVi: params?.id_don_vi ?? null,
    idKho: params?.id_kho ?? null,
    provinceCode: params?.province_code ?? null,
    fromDate: params?.from_date ?? "",
    toDate: params?.to_date ?? "",
  };
}

/**
 * Normalize backend `{ status, data, extra }` or `{ data, meta }` into a
 * stable DashboardEnvelope for widgets.
 */
export function normalizeDashboardEnvelope<T>(
  widget: DashboardWidgetName,
  payload: AxiosDataEnvelope<T> | null | undefined,
  fallbackParams?: Partial<DashboardScopeQueryParams>
): DashboardEnvelope<T> {
  const safePayload = payload && typeof payload === "object" ? payload : null;
  const fromExtra = safePayload?.extra?.meta;
  const cacheRaw =
    safePayload?.meta?.cache ??
    fromExtra?.cache ??
    safePayload?.extra?.cache ??
    emptyCacheMeta();
  const scopeRaw =
    safePayload?.meta?.scope ??
    fromExtra?.scope ??
    safePayload?.extra?.scope ??
    emptyScopeMeta(fallbackParams);

  const cache = keysToCamelCase(cacheRaw);
  const scope = keysToCamelCase(scopeRaw);

  return {
    data: adaptDashboardWidgetData<T>(widget, safePayload?.data ?? null),
    meta: { cache, scope },
  };
}

function scopeToParams(scope: DashboardScope): DashboardScopeQueryParams {
  const params: DashboardScopeQueryParams = {
    scope_type: scope.type,
    from_date: scope.fromDate,
    to_date: scope.toDate,
  };

  // Prefer explicit scope unit; fall back to current user/system tenant for
  // non-national scopes so id_don_vi tracks user switches.
  let idDonVi = scope.idDonVi ?? null;
  if (idDonVi == null && scope.type !== "national") {
    idDonVi = getIdDonViFromLocalStorage();
  }

  if (idDonVi != null) {
    params.id_don_vi = idDonVi;
  }

  if (scope.idKho != null) {
    params.id_kho = scope.idKho;
  }

  if (scope.provinceCode) {
    params.province_code = scope.provinceCode;
  }

  if (DASHBOARD_SKIP_CACHE) {
    params.skip_cache = 1;
  }

  return params;
}

type DashboardRequestParams = Partial<DashboardScopeQueryParams> & {
  group_by?: DashboardMovementGroupBy;
  metric?: DashboardMovementMetric | TopEntitiesData["metric"];
  entity_type?: "unit" | "warehouse" | "material";
  limit?: number;
  severity?: string | string[];
  cursor?: string;
};

async function getDashboard<T>(
  widget: DashboardWidgetName,
  path: string,
  params?: DashboardRequestParams
): Promise<DashboardEnvelope<T>> {
  const response = await apiServiceInstance().vueInstance.axios.get(
    `/api/v1/dashboard/${path}`,
    {
      params,
      // Poller must never trigger GET /api/v1/me/abilities.
      ...(path === "cache-versions"
        ? { meta: { skipAbilitiesSync: true } }
        : {}),
    }
  );

  try {
    return normalizeDashboardEnvelope<T>(widget, response.data, params);
  } catch (error) {
    const message =
      error instanceof Error ? error.message : "Unknown normalize error";
    throw new Error(`Dashboard "${widget}" normalize failed: ${message}`);
  }
}

export class DashboardService {
  static getMeta(): Promise<DashboardEnvelope<DashboardMetaData>> {
    return getDashboard<DashboardMetaData>("meta", "meta");
  }

  static getSummary(
    scope: DashboardScope
  ): Promise<DashboardEnvelope<DashboardSummaryData>> {
    return getDashboard<DashboardSummaryData>(
      "summary",
      "summary",
      scopeToParams(scope)
    );
  }

  static getSystemSummary(
    scope: DashboardScope
  ): Promise<DashboardEnvelope<DashboardSystemSummaryData>> {
    return getDashboard<DashboardSystemSummaryData>(
      "system-summary",
      "system-summary",
      scopeToParams(scope)
    );
  }

  static getMap(
    scope: DashboardScope
  ): Promise<DashboardEnvelope<DashboardMapData>> {
    return getDashboard<DashboardMapData>("map", "map", scopeToParams(scope));
  }

  static getInventoryMovement(
    scope: DashboardScope,
    options: {
      group_by?: DashboardMovementGroupBy;
      metric?: DashboardMovementMetric;
    } = {}
  ): Promise<DashboardEnvelope<InventoryMovementData>> {
    return getDashboard<InventoryMovementData>(
      "inventory-movement",
      "inventory-movement",
      {
        ...scopeToParams(scope),
        group_by: options.group_by ?? "day",
        metric: options.metric ?? "amount",
      }
    );
  }

  static getTicketStatuses(
    scope: DashboardScope
  ): Promise<DashboardEnvelope<DashboardTicketStatusesData>> {
    return getDashboard<DashboardTicketStatusesData>(
      "ticket-statuses",
      "ticket-statuses",
      scopeToParams(scope)
    );
  }

  static getAlerts(
    scope: DashboardScope,
    options: { severity?: string[]; limit?: number } = {}
  ): Promise<DashboardEnvelope<DashboardAlertsData>> {
    return getDashboard<DashboardAlertsData>("alerts", "alerts", {
      ...scopeToParams(scope),
      ...(options.severity?.length ? { severity: options.severity } : {}),
      ...(options.limit != null ? { limit: options.limit } : {}),
    });
  }

  static getTopEntities(
    scope: DashboardScope,
    options: {
      entityType?: "unit" | "warehouse" | "material";
      metric?:
        | "ending_amount"
        | "import_amount"
        | "export_amount"
        | "alert_count";
      limit?: number;
    } = {}
  ): Promise<DashboardEnvelope<TopEntitiesData>> {
    return getDashboard<TopEntitiesData>("top-entities", "top-entities", {
      ...scopeToParams(scope),
      entity_type: options.entityType ?? "material",
      metric: options.metric ?? "ending_amount",
      limit: options.limit ?? 5,
    });
  }

  /** @deprecated use getTopEntities */
  static getTopItems(
    scope: DashboardScope
  ): Promise<DashboardEnvelope<TopEntitiesData>> {
    return DashboardService.getTopEntities(scope, {
      entityType: "material",
      metric: "ending_amount",
    });
  }

  static getRecentActivities(
    scope: DashboardScope,
    options: { limit?: number; cursor?: string } = {}
  ): Promise<DashboardEnvelope<DashboardRecentActivitiesData>> {
    return getDashboard<DashboardRecentActivitiesData>(
      "recent-activities",
      "recent-activities",
      {
        ...scopeToParams(scope),
        ...(options.limit != null ? { limit: options.limit } : { limit: 5 }),
        ...(options.cursor ? { cursor: options.cursor } : {}),
      }
    );
  }

  static getCacheVersions(params?: {
    id_don_vi?: number;
    id_kho?: number;
  }): Promise<DashboardEnvelope<DashboardCacheVersions>> {
    const requestParams = {
      ...params,
      ...(DASHBOARD_SKIP_CACHE ? { skip_cache: 1 as const } : {}),
    };
    return getDashboard<DashboardCacheVersions>(
      "cache-versions",
      "cache-versions",
      requestParams
    );
  }
}
